import { useEffect, useMemo, useState } from "react";
import { AuditoinnitCacheKeys } from "../cacheKeys";
import * as Endpoint from "../../../endpoints";
import { getSgApiService } from "../../../sg-core/services/ApiService2";
import { useAuthContext } from "../../auth/AuthenticationProvider";
import { IAuditListFilters } from "../../interfaces/IAudit";
import { getAuditService } from "../../services/auditService";
import { IPagination } from "../../interfaces/common/IPagination";
import { useAuditCache } from "./useAuditsCache";
import { useSWROffline } from "../../../sg-core/swr/useSWROffline";
import { useIsMount } from "../../../sg-hooks/useIsMount";
import { defaultAuditFilters, defaultPagination } from "../../constants/controlledFiltters";
import { ConfigInterface } from "swr";
import { useUserAppRoles } from "../appRoles/useAppRoles";

export type IUseAuditPageHook = (pagination?: IPagination, filters?: Partial<IAuditListFilters>, config?: ConfigInterface<{ items: number[], totalItems: number }, Error>) => {
    data: number[];
    pagination: IPagination;
    goToPage: (page: number) => void;
    fetching: boolean;
}

export const useAuditPage: IUseAuditPageHook = (
    initialPagination = defaultPagination,
    filters = defaultAuditFilters,
    config = {}
) => {
    const { token, lang, anonymousSession } = useAuthContext();
    const { data: roles, error: roleLoadError, fetching: fetchingRoles } = useUserAppRoles();
    filters.roles = !roleLoadError && !fetchingRoles ? roles : undefined;

    const service = getAuditService(getSgApiService(token), Endpoint.auditoinnit, anonymousSession);
    const { recieveItems } = useAuditCache();
    const [pagination, setPagination] = useState<IPagination>(initialPagination);


    const fetcher = async (): Promise<{ items: number[], totalItems: number }> => {
        if (!filters.roles) { throw new Error("Missing user roles"); };
        const pageData = await service.getAuditPage(pagination, filters as any);
        if (pageData.isFailure) throw pageData.error;
        recieveItems(pageData.value.audits);
        return {
            items: pageData.value.audits.map(({ id }) => id),
            totalItems: pageData.value.totalItems
        };
    };

    const cacheKey = AuditoinnitCacheKeys(token, lang).AuditPage(pagination, filters);
    const { data, isValidating } = useSWROffline(cacheKey, fetcher, {
        suspense: true,
        revalidateOnFocus: false,
        ...config
    });


    const { totalItems, items } = data || {};

    const mounting = useIsMount();

    useEffect(() => {
        if (!mounting) {
            setPagination({ page: 1, pageSize: initialPagination.pageSize });
        }
    }, [filters]); //eslint-disable-line

    useEffect(() => { setPagination((prev) => ({ ...prev, totalItems })) }, [totalItems])

    useEffect(() => {
        const otherThanFirstPageHasNoItems = items && items.length < 1 && pagination.page > 1
        if (otherThanFirstPageHasNoItems) {
            setPagination((prev) => ({ ...prev, page: 1 }))
        }
    }, [items, pagination.page]);



    const rawData = items ?? [];

    return useMemo(() => ({
        data: rawData,
        pagination,
        goToPage: (page) => setPagination((prev) => ({ ...prev, page })),
        fetching: isValidating,
    }), [rawData, pagination, setPagination, isValidating]);
};
